import React from 'react';
import { toast } from 'sonner';
import { Button, Divider, Typography } from '@mui/material';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import type { StepConfigFor } from '../getStepConfig.types.ts';

const getDescription = ({
  isHealthcareProvider,
  step,
  description,
}: {
  isHealthcareProvider: boolean;
  /**
   * TODO: make this IntakeStatusBaseSteps[MHR_ACCESS] when config type is fixed
   */
  step: IntakeStatusBaseSteps[keyof IntakeStatusBaseSteps] | undefined;
  description: string;
}) => {
  if (step?.id !== 'MHR_ACCESS') {
    return null;
  }

  if (!isHealthcareProvider) {
    return description;
  }

  if (!step) {
    return description;
  }

  const { metadata } = step.currentStatus;

  if (!metadata) {
    return description;
  }

  const { accessCode } = metadata;

  if (!accessCode) {
    return description;
  }

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    navigator.clipboard.writeText(accessCode);

    toast.success("Patient's MHR Access Code copied to clipboard");
  };

  return (
    <Typography variant="body2" component="div" display="block" alignItems="center" gap={1}>
      {description}
      <Divider orientation="vertical" sx={{ display: 'inline-flex', height: '0.6rem', mx: 1 }} />
      <Button onClick={handleCopy} sx={{ fontSize: '0.6rem', p: 0, px: 0.5 }} variant="outlined" size="small">
        Copy code
      </Button>
    </Typography>
  );
};

export const MHRAccessStepConfig = {
  title: ({ isHealthcareProvider }) =>
    isHealthcareProvider ? 'My Health Record Access' : 'Grant Access to Your My Health Record',

  statuses: {
    NOT_STARTED: {
      label: 'Access Needed',
      cta: 'Grant Access Now',
      description: ({ isHealthcareProvider, step }) =>
        getDescription({
          isHealthcareProvider,
          step,
          description: isHealthcareProvider
            ? 'The patient is tasked with granting access to their My Health Record'
            : 'Please grant us access to your My Health Record',
        }),
      context: ({ isHealthcareProvider }) =>
        isHealthcareProvider
          ? 'Patient needs to grant access to their My Health Record'
          : 'You need to grant us access to your My Health Record',
    },

    PENDING: {
      label: 'Access Pending',
      cta: 'Resubmit',
      description: ({ isHealthcareProvider, step }) =>
        getDescription({
          isHealthcareProvider,
          step,
          description: isHealthcareProvider
            ? 'The patient has submitted their My Health Record access request. Please review it and verify their details'
            : 'You have submitted your My Health Record access request. We are reviewing it and will update you shortly',
        }),
      context: ({ isHealthcareProvider }) =>
        isHealthcareProvider
          ? 'The patient has submitted their My Health Record access request. We need to review it and update the status'
          : 'Your My Health Record access request is being reviewed. We will update you shortly',
    },

    COMPLETED: {
      label: 'Access Granted',
      cta: 'View',
      description: ({ isHealthcareProvider, step }) =>
        getDescription({
          isHealthcareProvider,
          step,
          description: isHealthcareProvider
            ? 'The patient has successfully granted access to their My Health Record'
            : 'You have successfully granted us access to your My Health Record',
        }),
      context: ({ isHealthcareProvider }) =>
        isHealthcareProvider
          ? 'The patient has successfully granted access to their My Health Record'
          : 'You have granted access to your My Health Record',
    },

    ERROR: {
      label: 'Access Failed',
      cta: 'Resubmit Now',
      description: ({ isHealthcareProvider, step }) =>
        getDescription({
          isHealthcareProvider,
          step,
          description: isHealthcareProvider
            ? 'There was an issue with the My Health Record access. The patient will need to resubmit it'
            : 'There was an issue with your My Health Record access. Please resubmit it',
        }),
      context: ({ isHealthcareProvider }) =>
        isHealthcareProvider
          ? 'There was an issue with the My Health Record access. The patient will need to resubmit it'
          : 'There was an issue with your My Health Record access. Please resubmit it',
    },
  },
} satisfies StepConfigFor<'MHR_ACCESS'>;
